import { Link, useNavigate, useLocation } from "react-router-dom";
import { LogOut, LayoutDashboard, Users, GitBranch, ClipboardList } from "lucide-react";
import { Logo } from "@/components/marketing/Nav";
import { ReadinessBadge } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/agency", label: "Dashboard", Icon: LayoutDashboard, exact: true },
  { to: "/agency/pipeline", label: "Pipeline", Icon: GitBranch },
  { to: "/agency/clients", label: "Clients", Icon: Users },
  { to: "/agency/reviews", label: "Review queue", Icon: ClipboardList },
];

export default function AgencyShell({ children, title, actions }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return (
    <div className="min-h-screen bg-softcream lg:flex">
      <aside className="hidden w-60 shrink-0 border-r border-hborder bg-navy lg:flex lg:flex-col">
        <div className="border-b border-white/10 p-5"><Logo dark /></div>
        <nav className="flex-1 space-y-1 p-3" aria-label="Agency">
          {NAV.map(({ to, label, Icon, exact }) => {
            const active = exact ? pathname === to : pathname.startsWith(to);
            return (
              <Link key={to} to={to} data-testid={`agency-nav-${label.toLowerCase().split(" ")[0]}`}
                className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${active ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5 hover:text-white"}`}>
                <Icon size={17} /> {label}
              </Link>
            );
          })}
        </nav>
        <div className="border-t border-white/10 p-3">
          <p className="px-3 text-xs text-white/50">{user?.name}</p>
          <button onClick={() => { logout(); navigate("/"); }} className="mt-1 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-white/70 hover:bg-white/5" data-testid="agency-logout">
            <LogOut size={17} /> Sign out
          </button>
        </div>
      </aside>

      {/* mobile top bar */}
      <div className="flex items-center justify-between border-b border-hborder bg-navy px-4 py-3 lg:hidden">
        <Logo dark />
        <button onClick={() => { logout(); navigate("/"); }} className="text-white/80" aria-label="Sign out"><LogOut size={18} /></button>
      </div>

      <div className="flex-1 overflow-x-hidden">
        <div className="border-b border-hborder bg-white px-4 py-4 sm:px-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h1 className="font-serif text-2xl tracking-tight text-heading">{title}</h1>
            {actions}
          </div>
        </div>
        <div className="p-4 sm:p-8">{children}</div>
        <nav className="sticky bottom-0 flex border-t border-hborder bg-white lg:hidden" aria-label="Agency mobile">
          {NAV.map(({ to, label, Icon, exact }) => {
            const active = exact ? pathname === to : pathname.startsWith(to);
            return <Link key={to} to={to} className={`flex flex-1 flex-col items-center gap-0.5 py-2.5 text-[11px] ${active ? "text-tblue" : "text-muted"}`}><Icon size={18} />{label.split(" ")[0]}</Link>;
          })}
        </nav>
      </div>
    </div>
  );
}

export function CaseTable({ cases, emptyLabel = "No cases" }) {
  if (!cases?.length) return <p className="rounded-md border border-dashed border-hborder bg-white p-8 text-center text-sm text-muted">{emptyLabel}</p>;
  return (
    <div className="overflow-x-auto rounded-md border border-hborder bg-white hv-scroll-thin">
      <table className="w-full min-w-[640px] text-left text-sm">
        <thead className="border-b border-hborder bg-softcream text-xs uppercase tracking-wider text-muted">
          <tr><th className="px-4 py-3">Applicant</th><th className="px-4 py-3">Destination</th><th className="px-4 py-3">Readiness</th><th className="px-4 py-3">Open findings</th><th className="px-4 py-3">Assignee</th></tr>
        </thead>
        <tbody>
          {cases.map((c) => (
            <tr key={c.id} className="border-b border-hborder/60 last:border-0 hover:bg-softcream/50" data-testid={`agency-case-${c.id}`}>
              <td className="px-4 py-3"><Link to={`/agency/cases/${c.id}`} className="font-medium text-navy hover:underline">{c.applicant_name || "Unknown"}</Link></td>
              <td className="px-4 py-3 text-muted">{c.corridor?.destination}</td>
              <td className="px-4 py-3"><ReadinessBadge state={c.readiness_state} /></td>
              <td className="px-4 py-3"><span className={c.open_findings ? "font-semibold text-coral" : "text-muted"}>{c.open_findings || 0}</span></td>
              <td className="px-4 py-3 text-muted">{c.assignee_name || "Unassigned"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
